import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../components/layout/AppLayout/AppLayout.jsx';
import CategoryTable from '../components/tables/CategoryTable.jsx';
import CategoryForm from '../components/forms/CategoryForm.jsx';
import Modal from '../../../components/ui/Modal/Modal.jsx';
import { useCategories } from '../hooks/useCategories.js';

const CategoriesPage = React.memo(({ onLogout }) => {
    const { t } = useTranslation(['inventory', 'common']);
    const categoriesHook = useCategories({
        limitOptions: [5, 10, 25, 50] // Categories-specific pagination options
    });
    
    // Modal state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    
    // Form state
    const [formLoading, setFormLoading] = useState(false);
    
    // Stable callback functions
    const handleAddCategory = useCallback(() => {
        setEditingCategory(null);
        setShowCreateForm(true);
    }, []);
    
    const handleEditCategory = useCallback((category) => {
        setEditingCategory(category);
        setShowCreateForm(true);
    }, []);
    
    const handleFormClose = useCallback(() => {
        setShowCreateForm(false);
        setEditingCategory(null);
    }, []);


    const handleCategorySubmit = useCallback(async (categoryData) => {
        setFormLoading(true);
        categoriesHook.setError('');

        try {
            let result;
            if (editingCategory) {
                result = await categoriesHook.updateCategory(editingCategory.id, categoryData);
            } else {
                result = await categoriesHook.createCategory(categoryData);
            }

            if (result.success) {
                handleFormClose();
                categoriesHook.setError('');
            } else {
                categoriesHook.setError(result.error);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            categoriesHook.setError('An unexpected error occurred. Please try again.');
        } finally {
            setFormLoading(false);
        }
    }, [editingCategory, categoriesHook, handleFormClose]);

    // Show loading state only for initial load
    if (categoriesHook.loading && (!categoriesHook.categories || categoriesHook.categories.length === 0)) {
        return (
            <AppLayout 
                title={t('inventory:categories')}
                error={categoriesHook.error}
                setError={categoriesHook.setError}
                onLogout={onLogout}
            >
                <div className="inventory-container">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>{t('common:messages.loading')}</p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout 
            title={t('inventory:categories')}
            error={categoriesHook.error}
            setError={categoriesHook.setError}
            onLogout={onLogout}
        >
            {/* Optimized CategoryTable handles all table logic */}
            <CategoryTable
                hookData={categoriesHook}
                onAddCategory={handleAddCategory}
                onEditCategory={handleEditCategory}
            />

            {/* Category Create/Edit Modal */}
            <Modal
                isOpen={showCreateForm}
                onClose={handleFormClose}
                title={editingCategory ? t('inventory:editCategory') : t('inventory:addCategory')}
                footer={
                    <>
                        <button 
                            type="button" 
                            onClick={handleFormClose} 
                            className="btn btn-secondary"
                            disabled={formLoading}
                        >
                            {t('common:buttons.cancel')}
                        </button>
                        <button 
                            type="submit" 
                            form="category-form"
                            className="btn btn-primary"
                            disabled={formLoading}
                        >
                            {formLoading ? (
                                <>
                                    <span className="spinner-small"></span>
                                    {editingCategory ? t('inventory:updatingCategory') : t('inventory:creatingCategory')}
                                </>
                            ) : (
                                editingCategory ? t('inventory:updateCategory') : t('inventory:createCategory')
                            )}
                        </button>
                    </>
                }
            >
                <CategoryForm
                    category={editingCategory}
                    onSubmit={handleCategorySubmit}
                    loading={formLoading}
                />
            </Modal>

            {/* Loading overlay for actions */}
            {categoriesHook.actionLoading && (
                <div className="action-loading-overlay">
                    <div className="spinner"></div>
                </div>
            )}
        </AppLayout>
    );
});

export default CategoriesPage;